import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getFirebaseApp } from './firebase';

function setCrewdleCookie(name: string, value: string): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `crewdle_${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;domain=.crewdle.ai;SameSite=Lax`;
  } catch {}
}

function getCrewdleCookie(name: string): string | null {
  try {
    const prefix = `crewdle_${name}=`;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.substring(prefix.length));
      }
    }
  } catch {}
  return null;
}

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private userDocUnsub: Unsubscribe | null = null;

  constructor(private translate: TranslocoService) {}

  getLanguage(): string {
    return localStorage.getItem('lang') ?? getCrewdleCookie('lang') ?? this.translate.getActiveLang();
  }

  setLanguage(language: string): void {
    localStorage.setItem('lang', language);
    setCrewdleCookie('lang', language);
    this.translate.setActiveLang(language);
    this.syncToFirestore(language);
  }

  async loadUserLanguage(): Promise<void> {
    try {
      const auth = getAuth(getFirebaseApp());
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const localLang = localStorage.getItem('lang');
      const db = getFirestore(getFirebaseApp());
      const snapshot = await getDoc(doc(db, 'users', uid));
      const firestoreLang = snapshot.exists() ? snapshot.data()['language'] : null;

      if (localLang && ['en', 'fr'].includes(localLang)) {
        // Local preference exists — push it to Firestore if different
        if (firestoreLang !== localLang) {
          await setDoc(doc(db, 'users', uid), { language: localLang }, { merge: true });
        }
        setCrewdleCookie('lang', localLang);
        this.translate.setActiveLang(localLang);
      } else if (firestoreLang && ['en', 'fr'].includes(firestoreLang)) {
        // No local preference — use Firestore value
        localStorage.setItem('lang', firestoreLang);
        setCrewdleCookie('lang', firestoreLang);
        this.translate.setActiveLang(firestoreLang);
      }
    } catch {}
  }

  /**
   * Apply a language received from Firestore (e.g. agent-driven change).
   * Updates local caches but does not re-sync to Firestore.
   */
  private applyRemoteLanguage(lang: string): void {
    if (lang === this.translate.getActiveLang()) return;
    localStorage.setItem('lang', lang);
    setCrewdleCookie('lang', lang);
    this.translate.setActiveLang(lang);
  }

  startUserSync(uid: string): void {
    this.stopUserSync();
    const db = getFirestore(getFirebaseApp());
    this.userDocUnsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      if (!snap.exists()) return;
      const remote = snap.data()['language'];
      if (remote && ['en', 'fr'].includes(remote)) {
        this.applyRemoteLanguage(remote);
      }
    });
  }

  stopUserSync(): void {
    if (this.userDocUnsub) {
      this.userDocUnsub();
      this.userDocUnsub = null;
    }
  }

  private async syncToFirestore(lang: string): Promise<void> {
    try {
      const auth = getAuth(getFirebaseApp());
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const db = getFirestore(getFirebaseApp());
      await setDoc(doc(db, 'users', uid), { language: lang }, { merge: true });
    } catch {}
  }
}
